import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, X, FileCheck } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-[#5A3B31] uppercase tracking-wider">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

export function Step1AdvanceDetails({ data, set, patch }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Net Commission Amount ($)"><Input data-testid="f-net" type="number" value={data.net_commission_amount} onChange={set("net_commission_amount")} /></Field>
      <Field label="Advance Amount Requested ($)"><Input data-testid="f-adv" type="number" value={data.advance_amount_requested} onChange={set("advance_amount_requested")} /></Field>
      <Field label="Expected Closing Date"><Input data-testid="f-close" type="date" value={data.expected_closing_date} onChange={set("expected_closing_date")} /></Field>
      <Field label="Under Contract?">
        <RadioGroup value={data.under_contract} onValueChange={(v) => patch({ under_contract: v })} className="flex gap-6 mt-3">
          <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="yes" data-testid="uc-yes"/> Yes</label>
          <label className="flex items-center gap-2 text-sm"><RadioGroupItem value="no" data-testid="uc-no"/> No</label>
        </RadioGroup>
      </Field>
      <Field label="Property Type">
        <Select value={data.property_type} onValueChange={(v) => patch({ property_type: v })}>
          <SelectTrigger data-testid="f-ptype"><SelectValue placeholder="Choose type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Residential">Residential</SelectItem>
            <SelectItem value="Commercial">Commercial</SelectItem>
            <SelectItem value="Land">Land</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      <div className="sm:col-span-2 text-xs text-[#8C7A70]">
        Advance amount cannot exceed 75% of net commission.
      </div>
    </div>
  );
}

export function Step2Applicant({ data, set }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="First Name"><Input data-testid="f-first" value={data.first_name} onChange={set("first_name")} /></Field>
      <Field label="Last Name"><Input data-testid="f-last" value={data.last_name} onChange={set("last_name")} /></Field>
      <Field label="Phone Number"><Input data-testid="f-phone" value={data.phone} onChange={set("phone")} /></Field>
      <Field label="Email Address"><Input data-testid="f-email" type="email" value={data.email} onChange={set("email")} /></Field>
      <Field label="State Licensed In"><Input data-testid="f-state" value={data.state_licensed_in} onChange={set("state_licensed_in")} /></Field>
      <Field label="Real Estate License Number"><Input data-testid="f-license" value={data.license_number} onChange={set("license_number")} /></Field>
    </div>
  );
}

export function Step3Account({ data, set }) {
  return (
    <div className="grid grid-cols-1 gap-5">
      <Field label="Email Address"><Input data-testid="f-acc-email" type="email" value={data.account_email} onChange={set("account_email")} /></Field>
      <Field label="Password"><Input data-testid="f-pass" type="password" value={data.password} onChange={set("password")} /></Field>
      <Field label="Confirm Password"><Input data-testid="f-pass2" type="password" value={data.confirm_password} onChange={set("confirm_password")} /></Field>
      <p className="text-xs text-[#8C7A70]">Password requires 8+ characters, 1 uppercase, 1 number, and 1 symbol.</p>
    </div>
  );
}

export function Step4Brokerage({ data, set }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Brokerage Name"><Input data-testid="f-brk" value={data.brokerage_name} onChange={set("brokerage_name")} /></Field>
      <Field label="Position / Role"><Input data-testid="f-pos" value={data.position} onChange={set("position")} /></Field>
      <Field label="Office Phone"><Input data-testid="f-ofc" value={data.office_phone} onChange={set("office_phone")} /></Field>
      <Field label="Brokerage Address"><Input data-testid="f-brk-addr" value={data.brokerage_address} onChange={set("brokerage_address")} /></Field>
      <Field label="City"><Input data-testid="f-brk-city" value={data.brokerage_city} onChange={set("brokerage_city")} /></Field>
      <Field label="State"><Input data-testid="f-brk-state" value={data.brokerage_state} onChange={set("brokerage_state")} /></Field>
      <Field label="Zip Code"><Input data-testid="f-brk-zip" value={data.brokerage_zip} onChange={set("brokerage_zip")} /></Field>
    </div>
  );
}

export function Step5Property({ data, set }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Property Address"><Input data-testid="f-p-addr" value={data.property_address} onChange={set("property_address")} /></Field>
      <Field label="City"><Input data-testid="f-p-city" value={data.property_city} onChange={set("property_city")} /></Field>
      <Field label="State"><Input data-testid="f-p-state" value={data.property_state} onChange={set("property_state")} /></Field>
      <Field label="Zip Code"><Input data-testid="f-p-zip" value={data.property_zip} onChange={set("property_zip")} /></Field>
      <Field label="Estimated Closing Date"><Input data-testid="f-p-close" type="date" value={data.property_closing_date} onChange={set("property_closing_date")} /></Field>
      <Field label="Sale Price ($)"><Input data-testid="f-p-price" type="number" value={data.sale_price} onChange={set("sale_price")} /></Field>
      <Field label="Expected Commission ($)"><Input data-testid="f-p-comm" type="number" value={data.expected_commission} onChange={set("expected_commission")} /></Field>
    </div>
  );
}

export function Step6Broker({ data, set }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Broker First Name"><Input data-testid="f-b-first" value={data.broker_first_name} onChange={set("broker_first_name")} /></Field>
      <Field label="Broker Last Name"><Input data-testid="f-b-last" value={data.broker_last_name} onChange={set("broker_last_name")} /></Field>
      <Field label="Broker Email"><Input data-testid="f-b-email" type="email" value={data.broker_email} onChange={set("broker_email")} /></Field>
      <Field label="Broker Phone"><Input data-testid="f-b-phone" value={data.broker_phone} onChange={set("broker_phone")} /></Field>
    </div>
  );
}

export function Step7Closing({ data, set }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Closing Firm Name"><Input data-testid="f-cf-name" value={data.closing_firm_name} onChange={set("closing_firm_name")} /></Field>
      <Field label="Closing Officer First Name"><Input data-testid="f-co-first" value={data.closing_officer_first_name} onChange={set("closing_officer_first_name")} /></Field>
      <Field label="Closing Officer Last Name"><Input data-testid="f-co-last" value={data.closing_officer_last_name} onChange={set("closing_officer_last_name")} /></Field>
      <Field label="Closing Officer Email"><Input data-testid="f-co-email" type="email" value={data.closing_officer_email} onChange={set("closing_officer_email")} /></Field>
      <Field label="Closing Officer Phone"><Input data-testid="f-co-phone" value={data.closing_officer_phone} onChange={set("closing_officer_phone")} /></Field>
    </div>
  );
}

const UPLOAD_LABELS = [
  "Purchase Contract",
  "Commission Agreement",
  "Government ID",
  "Recent Closing / Transaction History",
];

function UploadRow({ label, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10 MB."); return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploaded = { label, ...data };
      setDone(uploaded);
      onUploaded(uploaded);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); }
  };

  return (
    <label className={`flex items-center justify-between gap-4 p-5 rounded-2xl border cursor-pointer transition ${done ? "border-emerald-300 bg-emerald-50/40" : "border-[#E8DFD8] hover:border-[#CBA4A4]"}`}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[#2A1C14]">{label}</p>
        {done ? (
          <p className="text-xs text-emerald-800 mt-1 truncate">{done.original_filename}</p>
        ) : (
          <p className="text-xs text-[#8C7A70]">PDF, JPG, PNG, or WEBP up to 10 MB</p>
        )}
      </div>
      <span className={`text-sm py-2 px-4 rounded-full inline-flex items-center gap-2 ${done ? "bg-emerald-600 text-white" : "btn-blush"}`}>
        {done ? (<><FileCheck size={16} strokeWidth={1.5}/> Uploaded</>) : (busy ? "Uploading..." : (<><Upload size={16} strokeWidth={1.5}/> Upload</>))}
      </span>
      <input data-testid={`f-upload-${label}`} type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} disabled={busy}/>
    </label>
  );
}

export function Step8Uploads({ data, onUploaded }) {
  return (
    <div className="space-y-6">
      <p className="text-sm text-[#5A3B31]">Upload the following documents securely. Files are encrypted and only visible to your reviewer.</p>
      {UPLOAD_LABELS.map((label) => (
        <UploadRow key={label} label={label} onUploaded={onUploaded} />
      ))}
      {data.uploaded_files?.length > 0 && (
        <div className="text-sm mt-4">
          <p className="overline mb-2">{data.uploaded_files.length} file{data.uploaded_files.length === 1 ? "" : "s"} uploaded</p>
        </div>
      )}
    </div>
  );
}

const CONSENT_ITEMS = [
  { k: "certify_accurate", t: "I certify that the information provided is accurate." },
  { k: "authorize_verification", t: "I authorize Hannah Howard Funding to contact my broker and/or closing officer for transaction verification." },
  { k: "consent_communications", t: "I consent to receive emails, calls, and SMS messages regarding my application. Message and data rates may apply. Reply STOP to opt out." },
];

// NOTE: Step9 is now rendered from src/components/consent/ConsentControls.jsx
// with the full five-required-plus-one-optional consent structure required by
// the Legal Package v2.0. The legacy CONSENT_ITEMS above is kept only for
// reference and is no longer imported anywhere.
